import React from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import AnalyticsRoundedIcon from "@mui/icons-material/AnalyticsRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ListAltRoundedIcon from "@mui/icons-material/ListAltRounded";
import NotificationsActiveRoundedIcon from "@mui/icons-material/NotificationsActiveRounded";
import PaymentsRoundedIcon from "@mui/icons-material/PaymentsRounded";
import PublicRoundedIcon from "@mui/icons-material/PublicRounded";
import PersonAddAltRoundedIcon from "@mui/icons-material/PersonAddAltRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import SettingsSuggestRoundedIcon from "@mui/icons-material/SettingsSuggestRounded";
import WarehouseRoundedIcon from "@mui/icons-material/WarehouseRounded";
import WorkspacePremiumRoundedIcon from "@mui/icons-material/WorkspacePremiumRounded";
import { useNavigate, useParams } from "react-router-dom";
import { projectsApi } from "../../api/projects";
import { useAuth } from "../../context/AuthContext";
import { useProjects } from "../../context/ProjectsContext";
import {
  PROJECT_MODULES,
  applyModuleChange,
  normalizeProjectModules,
  normalizeProjectPlanCode,
  serializeModulesForApi,
} from "../../utils/projectModules";
import { isSuperAdmin } from "../../utils/roles";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };

const MODULE_ICONS = {
  landing: <PublicRoundedIcon />,
  products: <Inventory2RoundedIcon />,
  orders: <ListAltRoundedIcon />,
  ecommerce: <PaymentsRoundedIcon />,
  system: <WarehouseRoundedIcon />,
  stats: <AnalyticsRoundedIcon />,
  webpush: <NotificationsActiveRoundedIcon />,
};

function memberId(member) {
  return member.id || member._id;
}

function memberUser(member) {
  return member.userId || member.user || {};
}

function moduleActionPath(projectId, key) {
  if (key === "landing") return `/dashboard/proyectos/${projectId}/pagina`;
  if (key === "products") return `/dashboard/proyectos/${projectId}/productos`;
  if (key === "orders") return `/dashboard/proyectos/${projectId}/pedidos`;
  if (key === "ecommerce") return `/dashboard/proyectos/${projectId}/configuracion/ecommerce`;
  if (key === "system") return `/dashboard/proyectos/${projectId}/sistema`;
  if (key === "stats") return `/dashboard/proyectos/${projectId}/estadisticas`;
  if (key === "webpush") return `/dashboard/proyectos/${projectId}/configuracion/notificaciones`;
  return "";
}

export default function ProjectDetailPage({ embedded = false, section = "all" }) {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { loadProjects, setActiveProject } = useProjects();
  const canEditTechnical = isSuperAdmin(user);

  const [project, setProject] = React.useState(null);
  const [member, setMember] = React.useState(null);
  const [members, setMembers] = React.useState([]);
  const [form, setForm] = React.useState({
    name: "",
    plan: "catalogo",
    status: "active",
    domains: "",
    modules: normalizeProjectModules({}),
  });
  const [memberForm, setMemberForm] = React.useState({ email: "", role: "viewer" });
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const [message, setMessage] = React.useState("");

  const canManageMercadoPago = canEditTechnical || ["owner", "admin"].includes(member?.role);
  const canManageProjectAccess = canManageMercadoPago;

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await projectsApi.get(projectId);
      const nextProject = data.project;
      setProject(nextProject);
      setMember(data.member || null);
      if (nextProject) setActiveProject(nextProject);
      setForm({
        name: nextProject.name || "",
        plan: normalizeProjectPlanCode(nextProject.billing?.planCode || nextProject.plan || "catalogo"),
        status: nextProject.status || "active",
        domains: (nextProject.domains || []).join("\n"),
        modules: normalizeProjectModules(nextProject.modules),
      });

      try {
        const membersData = await projectsApi.listMembers(projectId);
        setMembers(membersData.members || []);
      } catch (_membersError) {
        setMembers([]);
      }
    } catch (err) {
      setError(err.message || "No se pudo cargar el proyecto");
    } finally {
      setLoading(false);
    }
  }, [projectId, setActiveProject]);

  React.useEffect(() => {
    load();
  }, [load]);

  const change = (field, value) => {
    setForm((previous) => ({ ...previous, [field]: value }));
  };

  const changeModule = (key, checked) => {
    setForm((previous) => ({
      ...previous,
      modules: applyModuleChange(previous.modules, key, checked),
    }));
  };

  const save = async (event) => {
    event?.preventDefault?.();
    if (!canEditTechnical) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      const payload = {
        name: form.name,
        plan: form.plan,
        status: form.status,
        domains: form.domains,
        modules: serializeModulesForApi(form.modules),
      };
      const data = await projectsApi.update(projectId, payload);
      setProject(data.project);
      setActiveProject(data.project);
      setMessage(section === "modules" ? "Módulos actualizados." : "Configuración general actualizada.");
      await loadProjects();
      await load();
    } catch (err) {
      setError(err.message || "No se pudo guardar el proyecto");
    } finally {
      setSaving(false);
    }
  };

  const addMember = async (event) => {
    event.preventDefault();
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.addMember(projectId, memberForm);
      setMemberForm({ email: "", role: "viewer" });
      setMessage("Colaborador agregado.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo agregar el colaborador");
    } finally {
      setSaving(false);
    }
  };

  const removeMember = async (memberToRemove) => {
    const userInfo = memberUser(memberToRemove);
    const ok = window.confirm(`¿Quitar acceso a ${userInfo.email || "este usuario"}?`);
    if (!ok) return;
    try {
      setSaving(true);
      setError("");
      setMessage("");
      await projectsApi.removeMember(projectId, memberId(memberToRemove));
      setMessage("Colaborador eliminado.");
      await load();
    } catch (err) {
      setError(err.message || "No se pudo eliminar el colaborador");
    } finally {
      setSaving(false);
    }
  };

  const activeModules = PROJECT_MODULES.filter((module) => form.modules[module.key]);
  const showModules = section === "all" || section === "modules";
  const showGeneral = section === "all" || section === "general";
  const showAccess = section === "all" || section === "access";
  const subscriptionPath = `/dashboard/proyectos/${projectId}/configuracion/suscripcion`;

  return (
    <Stack spacing={3}>
      {!embedded && (
        <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
          <Box>
            <Typography variant="h3" sx={{ fontWeight: 950, color: BRAND.dark }}>
              {project?.name || "Proyecto"}
            </Typography>
            <Typography sx={{ color: "rgba(25,36,47,0.65)", mt: 0.5 }}>
              {project ? `/${project.slug} · DB: ${project.dbName}` : "Cargando configuración..."}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button
              startIcon={<WorkspacePremiumRoundedIcon />}
              onClick={() => navigate(subscriptionPath)}
              variant="outlined"
              sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900, color: BRAND.orange }}
            >
              {canEditTechnical ? "Plan del cliente" : "Mejorar plan"}
            </Button>
            {form.modules.landing && (
              <Button
                startIcon={<PublicRoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/pagina`)}
                variant="outlined"
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Landing
              </Button>
            )}
            {form.modules.products && (
              <Button
                startIcon={<Inventory2RoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/productos`)}
                variant="outlined"
                sx={{ borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Productos
              </Button>
            )}
            {form.modules.orders && (
              <Button
                startIcon={<ListAltRoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/pedidos`)}
                variant="contained"
                sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
              >
                Pedidos
              </Button>
            )}
          </Stack>
        </Stack>
      )}

      {loading && <Alert severity="info">Cargando proyecto...</Alert>}
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}

      {showModules && (
      <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
        <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2} sx={{ mb: 2.5 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Niveles y módulos del proyecto
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.5 }}>
                Landing → Productos → Pedidos → E-commerce. Sistema y Estadísticas se habilitan según la operación contratada.
              </Typography>
            </Box>
            <Chip
              icon={<SettingsSuggestRoundedIcon />}
              label={`${activeModules.length} de ${PROJECT_MODULES.length} activos`}
              color="primary"
              variant="outlined"
              sx={{ fontWeight: 900, alignSelf: "flex-start" }}
            />
          </Stack>

          {!canEditTechnical && (
            <Alert
              severity="info"
              sx={{ mb: 2.5 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => navigate(subscriptionPath)}
                  sx={{ textTransform: "none", fontWeight: 900 }}
                >
                  Ver servicios
                </Button>
              }
            >
              Tu suscripción actual habilita estas secciones. Para activar otro módulo, administrá tus servicios mensuales.
            </Alert>
          )}

          <Grid container spacing={2}>
            {PROJECT_MODULES.map((module) => {
              const enabled = Boolean(form.modules[module.key]);
              const canOpen = enabled && (module.key !== "ecommerce" || canManageMercadoPago);
              return (
                <Grid item xs={12} md={6} xl={module.key === "stats" ? 12 : 6} key={module.key}>
                  <Card
                    elevation={0}
                    sx={{
                      height: "100%",
                      borderRadius: 3.5,
                      border: `1px solid ${enabled ? "rgba(243,90,58,0.35)" : "rgba(25,36,47,0.08)"}`,
                      bgcolor: enabled ? "rgba(243,90,58,0.035)" : "#FAFAFC",
                    }}
                  >
                    <CardContent sx={{ p: 2.5 }}>
                      <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
                        <Stack direction="row" spacing={1.5} alignItems="flex-start">
                          <Box sx={{ width: 46, height: 46, flexShrink: 0, borderRadius: 3, display: "grid", placeItems: "center", bgcolor: enabled ? "rgba(243,90,58,0.12)" : "rgba(25,36,47,0.06)", color: enabled ? BRAND.orange : "rgba(25,36,47,0.5)" }}>
                            {MODULE_ICONS[module.key]}
                          </Box>
                          <Box>
                            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                              <Chip size="small" label={`Nivel ${module.level}`} sx={{ fontWeight: 900 }} />
                              <Typography variant="h6" sx={{ fontWeight: 950, color: BRAND.dark }}>
                                {module.label}
                              </Typography>
                            </Stack>
                            <Typography variant="body2" sx={{ color: "rgba(25,36,47,0.64)", mt: 0.75 }}>
                              {module.description}
                            </Typography>
                          </Box>
                        </Stack>
                        <Switch
                          disabled={!canEditTechnical}
                          checked={enabled}
                          onChange={(event) => changeModule(module.key, event.target.checked)}
                          inputProps={{ "aria-label": `Habilitar ${module.label}` }}
                        />
                      </Stack>

                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                        {module.includes.map((item) => (
                          <Chip key={item} size="small" label={item} variant="outlined" />
                        ))}
                      </Stack>

                      {canOpen && (
                        <Button
                          onClick={() => navigate(moduleActionPath(projectId, module.key))}
                          sx={{ mt: 1.5, p: 0, minWidth: 0, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                        >
                          {module.key === "ecommerce" ? "Configurar Mercado Pago" : module.key === "webpush" ? "Administrar notificaciones" : `Abrir ${module.label}`}
                        </Button>
                      )}
                      {!enabled && !canEditTechnical && (
                        <Button
                          startIcon={<WorkspacePremiumRoundedIcon />}
                          onClick={() => navigate(`${subscriptionPath}?feature=${encodeURIComponent(module.label)}`)}
                          sx={{ mt: 1.5, p: 0, minWidth: 0, color: BRAND.orange, textTransform: "none", fontWeight: 900 }}
                        >
                          Contratar servicio para activar
                        </Button>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          {canEditTechnical && (
            <Button
              type="button"
              disabled={saving}
              startIcon={<SaveRoundedIcon />}
              onClick={save}
              variant="contained"
              sx={{ mt: 2.5, bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}
            >
              {saving ? "Guardando..." : "Guardar funcionalidades"}
            </Button>
          )}
        </CardContent>
      </Card>
      )}

      {(showGeneral || showAccess) && (
      <Grid container spacing={3}>
        {showGeneral && (
        <Grid item xs={12} lg={showAccess ? 7 : 12}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark, mb: 2 }}>
                Datos generales del proyecto
              </Typography>

              <Box component="form" onSubmit={save}>
                <Stack spacing={2}>
                  <TextField label="Nombre comercial" value={form.name} onChange={(event) => change("name", event.target.value)} disabled={!canEditTechnical} fullWidth />
                  <TextField select label="Estado operativo" value={form.status} onChange={(event) => change("status", event.target.value)} disabled={!canEditTechnical} fullWidth>
                    <MenuItem value="active">Activo</MenuItem>
                    <MenuItem value="paused">Pausado</MenuItem>
                    <MenuItem value="cancelled">Cancelado</MenuItem>
                  </TextField>
                  <Alert
                    severity="info"
                    action={
                      <Button
                        type="button"
                        color="inherit"
                        size="small"
                        onClick={() => navigate(subscriptionPath)}
                        sx={{ textTransform: "none", fontWeight: 900 }}
                      >
                        Administrar plan
                      </Button>
                    }
                  >
                    El plan, la mensualidad y el medio de pago se administran juntos desde Plan y suscripción.
                  </Alert>
                  <TextField
                    label="Dominios"
                    value={form.domains}
                    onChange={(event) => change("domains", event.target.value)}
                    disabled={!canEditTechnical}
                    multiline
                    minRows={3}
                    helperText="Uno por línea o separados por coma. Ej: cliente.com.ar"
                    fullWidth
                  />

                  <Divider />

                  <Alert
                    severity="info"
                    action={
                      form.modules.landing ? (
                        <Button
                          type="button"
                          color="inherit"
                          size="small"
                          onClick={() => navigate(`/dashboard/proyectos/${projectId}/pagina`)}
                          sx={{ textTransform: "none", fontWeight: 900 }}
                        >
                          Editar identidad
                        </Button>
                      ) : null
                    }
                  >
                    Logos, favicon, colores y tipografías se administran desde Landing &gt; Ajustes. Esa configuración es la fuente pública del sitio.
                  </Alert>

                  {canEditTechnical && (
                    <Button type="submit" disabled={saving} startIcon={<SaveRoundedIcon />} variant="contained" sx={{ alignSelf: "flex-start", bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                      {saving ? "Guardando..." : "Guardar configuración"}
                    </Button>
                  )}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        )}

        {showAccess && (
        <Grid item xs={12} lg={showGeneral ? 5 : 12}>
          <Card elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(25,36,47,0.08)" }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 950, color: BRAND.dark }}>
                Accesos del proyecto
              </Typography>
              <Typography sx={{ color: "rgba(25,36,47,0.62)", mt: 0.5, mb: 2 }}>
                El cliente sólo entra a este proyecto. WebProfit administra todos.
              </Typography>

              {!canManageProjectAccess && (
                <Alert severity="info" sx={{ mb: 2 }}>Sólo el owner o un administrador puede modificar los accesos.</Alert>
              )}

              {canManageProjectAccess && (
              <Box component="form" onSubmit={addMember} sx={{ mb: 2 }}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <TextField size="small" label="Email" type="email" value={memberForm.email} onChange={(event) => setMemberForm((previous) => ({ ...previous, email: event.target.value }))} fullWidth required />
                  <TextField select size="small" label="Rol" value={memberForm.role} onChange={(event) => setMemberForm((previous) => ({ ...previous, role: event.target.value }))} sx={{ minWidth: 145 }}>
                    <MenuItem value="owner">Owner</MenuItem>
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="vendedor">Vendedor</MenuItem>
                    <MenuItem value="deposito">Depósito</MenuItem>
                    <MenuItem value="facturacion">Facturación</MenuItem>
                    <MenuItem value="viewer">Lectura</MenuItem>
                  </TextField>
                  <Button type="submit" disabled={saving} startIcon={<PersonAddAltRoundedIcon />} variant="contained" sx={{ bgcolor: BRAND.orange, borderRadius: 999, textTransform: "none", fontWeight: 900 }}>
                    Agregar
                  </Button>
                </Stack>
              </Box>
              )}

              <Stack spacing={1.5}>
                {members.length === 0 && <Alert severity="info">No se pudieron cargar miembros o todavía no hay colaboradores.</Alert>}
                {members.map((projectMember) => {
                  const memberUserInfo = memberUser(projectMember);
                  return (
                    <Box key={memberId(projectMember)} sx={{ p: 2, borderRadius: 3, bgcolor: "#F6F7FB", border: "1px solid rgba(25,36,47,0.06)" }}>
                      <Stack direction="row" justifyContent="space-between" spacing={1.5}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography noWrap sx={{ fontWeight: 950, color: BRAND.dark }}>{memberUserInfo.name || "Usuario"}</Typography>
                          <Typography noWrap variant="body2" sx={{ color: "rgba(25,36,47,0.62)" }}>{memberUserInfo.email}</Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                            <Chip size="small" label={projectMember.role} />
                            <Chip size="small" label={memberUserInfo.status || "—"} color={memberUserInfo.status === "active" ? "success" : "default"} />
                          </Stack>
                        </Box>
                        {canManageProjectAccess && projectMember.role !== "owner" && (
                          <Button onClick={() => removeMember(projectMember)} color="error" startIcon={<DeleteRoundedIcon />} sx={{ textTransform: "none", fontWeight: 900 }}>
                            Quitar
                          </Button>
                        )}
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        )}
      </Grid>
      )}
    </Stack>
  );
}
